import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './CreditPurchaseModal.css';

const CreditPurchaseModal = ({ isOpen, onClose, onPurchaseSuccess, user, onOpenAuthModal }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [selectedPackagePreview, setSelectedPackagePreview] = useState(null);
  
  // Misafir kullanıcı kontrolü
  const isGuest = user?.email?.includes('guest_') || user?.user_type === 'guest';

  useEffect(() => {
    if (isOpen) {
      fetchPackages();
      fetchBalance();
      checkPaymentStatus();
    }
  }, [isOpen]);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/credits/packages');
      setPackages(response.data.packages || []);
      setStripeEnabled(response.data.stripe_enabled || false);
    } catch (err) {
      console.error('Paketler yüklenemedi:', err);
      setError('Paketler yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/credits/packages/balance', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCreditBalance(response.data);
    } catch (err) {
      console.error('Bakiye yüklenemedi:', err);
    }
  };

  // URL'den payment status kontrol et
  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const payment = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (payment === 'success' && sessionId) {
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`/api/credits/packages/stripe/verify/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          setSuccess({
            credits: response.data.credits_added,
            message: response.data.message
          });
          fetchBalance();
          if (onPurchaseSuccess) {
            onPurchaseSuccess(response.data);
          }
        }
        // URL'den parametreleri temizle
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (err) {
        console.error('Payment verification error:', err);
        setError('Ödeme doğrulanamadı. Lütfen destek ile iletişime geçin.');
      }
    } else if (payment === 'cancelled') {
      setError('Ödeme iptal edildi');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const handlePurchase = async (pkg) => {
    try {
      setPurchasing(true);
      setSelectedPackage(pkg.id);
      setError(null);
      
      const token = localStorage.getItem('access_token');

      if (!token) {
        setError('Lütfen önce giriş yapın');
        return;
      }

      if (stripeEnabled) {
        // Stripe Checkout
        const response = await axios.post('/api/credits/packages/stripe/checkout', 
          { 
            package_id: pkg.id,
            success_url: `${window.location.origin}/study/chat?payment=success&session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: `${window.location.origin}/study/chat?payment=cancelled`
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.checkout_url) {
          // Stripe Checkout sayfasına yönlendir
          window.location.href = response.data.checkout_url;
        }
      } else {
        setError('Ödeme sistemi şu an aktif değil. Lütfen daha sonra tekrar deneyin.');
      }
    } catch (err) {
      console.error('Satın alma hatası:', err);
      const errorMessage = err.response?.data?.detail || 'Satın alma işlemi başarısız';
      setError(errorMessage);
    } finally {
      setPurchasing(false);
      setSelectedPackage(null);
    }
  };

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    onClose();
  };

  if (!isOpen) return null;

  // Misafir kullanıcı için kayıt uyarısı göster
  if (isGuest) {
    return (
      <div className="cpm-overlay" onClick={handleClose}>
        <div className="cpm-modal" onClick={e => e.stopPropagation()} style={{maxWidth: '440px', padding: '0'}}>
          <button className="cpm-close" onClick={handleClose}>×</button>
          
          {/* Header */}
          <div style={{
            padding: '32px 32px 24px',
            textAlign: 'center',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
              fontSize: '28px'
            }}>
              🔐
            </div>
            <h2 style={{
              margin: '0 0 8px',
              fontSize: '24px',
              fontWeight: '600',
              color: '#1a1a1a'
            }}>Kayıt Olun</h2>
            <p style={{
              margin: 0,
              fontSize: '14px',
              color: '#666',
              lineHeight: '1.5'
            }}>
              Kredi satın almak için önce kayıt olmanız gerekiyor
            </p>
          </div>

          {/* Benefits */}
          <div style={{padding: '24px 32px'}}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#f0f7ff',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0
                }}>✓</div>
                <span style={{fontSize: '14px', color: '#333'}}>750 aylık ücretsiz kredi</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#f0f7ff',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0
                }}>✓</div>
                <span style={{fontSize: '14px', color: '#333'}}>Kredi satın alabilme</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#f0f7ff',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0
                }}>✓</div>
                <span style={{fontSize: '14px', color: '#333'}}>Sohbet geçmişi kaydedilir</span>
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  background: '#f0f7ff',
                  borderRadius: '6px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0
                }}>✓</div>
                <span style={{fontSize: '14px', color: '#333'}}>Tüm özelliklere tam erişim</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{padding: '0 32px 32px'}}>
            <button
              onClick={() => {
                handleClose();
                if (onOpenAuthModal) {
                  onOpenAuthModal('register');
                }
              }}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                marginBottom: '10px',
                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
              }}
            >
              Hemen Kayıt Ol
            </button>

            <button
              onClick={handleClose}
              style={{
                width: '100%',
                padding: '12px',
                background: 'transparent',
                color: '#666',
                border: 'none',
                borderRadius: '10px',
                fontSize: '14px',
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.background = 'transparent'}
            >
              Daha Sonra
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cpm-overlay" onClick={handleClose}>
      <div className="cpm-modal" onClick={e => e.stopPropagation()}>
        <button className="cpm-close" onClick={handleClose}>×</button>
        
        <div className="cpm-header">
          <h2>⚡ Kredi Satın Al</h2>
          <p>Zeha ile daha fazla çalışmak için kredi paketi seçin</p>
        </div>

        {/* Misafir Kullanıcı Uyarısı */}
        {isGuest && (
          <div className="cpm-error" style={{marginBottom: '20px'}}>
            <strong>⚠️ Misafir Kullanıcı</strong>
            <p>Kredi satın almak için lütfen kayıt olun. Misafir hesaplar geçici olduğu için kredi satın alınamaz.</p>
          </div>
        )}

        {/* Mevcut Bakiye */}
        {creditBalance && !isGuest && (
          <div className="cpm-balance">
            <div className="cpm-balance-single">
              <span className="cpm-balance-label">Mevcut Krediniz</span>
              <span className="cpm-balance-value-large">{creditBalance.total_available?.toLocaleString() || 0}</span>
              <span className="cpm-balance-unit">kredi</span>
            </div>
            {selectedPackagePreview && (
              <>
                <div className="cpm-balance-arrow">→</div>
                <div className="cpm-balance-single cpm-balance-preview">
                  <span className="cpm-balance-label">Satın Aldıktan Sonra</span>
                  <span className="cpm-balance-value-large cpm-preview-value">
                    {((creditBalance.total_available || 0) + selectedPackagePreview.credits).toLocaleString()}
                  </span>
                  <span className="cpm-balance-unit">kredi</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Başarı Mesajı */}
        {success && (
          <div className="cpm-success">
            <span className="cpm-success-icon">🎉</span>
            <div className="cpm-success-content">
              <strong>{success.credits?.toLocaleString()} kredi eklendi!</strong>
              <p>Hesabınıza başarıyla yüklendi.</p>
            </div>
          </div>
        )}

        {/* Hata Mesajı */}
        {error && <div className="cpm-error">{error}</div>}

        {loading ? (
          <div className="cpm-loading">
            <div className="cpm-loading-spinner"></div>
            <p>Paketler yükleniyor...</p>
          </div>
        ) : (
          <div className="cpm-packages">
            {packages.map(pkg => (
              <div 
                key={pkg.id} 
                className={`cpm-package ${pkg.popular ? 'cpm-popular' : ''} ${selectedPackagePreview?.id === pkg.id ? 'cmp-selected' : ''}`}
                onMouseEnter={() => setSelectedPackagePreview(pkg)}
                onMouseLeave={() => setSelectedPackagePreview(null)}
              >
                {pkg.popular && <span className="cpm-badge">⭐ En Popüler</span>}
                <h3>{pkg.name}</h3>
                <div className="cpm-credits">
                  <span className="cpm-credits-value">{pkg.credits.toLocaleString()}</span>
                  <span className="cpm-credits-label">Kredi</span>
                </div>
                <div className="cpm-price">
                  <span className="cpm-currency">$</span>
                  <span className="cpm-price-value">{pkg.price}</span>
                </div>
                <div className="cpm-unit">
                  <span className="cpm-unit-price">${pkg.unit_price.toFixed(4)}</span>
                  <span className="cpm-unit-label">/kredi</span>
                </div>
                <p className="cpm-desc">{pkg.description}</p>
                
                {/* Tasarruf Göstergesi */}
                {pkg.id !== 'starter' && (
                  <div className="cpm-savings">
                    {pkg.id === 'standard' && '💰 %28 tasarruf'}
                    {pkg.id === 'premium' && '💰 %44 tasarruf'}
                    {pkg.id === 'mega' && '💰 %63 tasarruf'}
                  </div>
                )}
                
                <button 
                  className="cpm-buy-btn"
                  onClick={() => handlePurchase(pkg)}
                  disabled={purchasing || !stripeEnabled}
                >
                  {purchasing && selectedPackage === pkg.id ? (
                    <span className="cpm-spinner"></span>
                  ) : (
                    <>
                      <span>Satın Al</span>
                      <span className="cpm-btn-arrow">→</span>
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="cpm-footer">
          <div className="cpm-features">
            <div className="cpm-feature">
              <span>✓</span>
              <span>Satın alınan krediler sıfırlanmaz</span>
            </div>
            <div className="cpm-feature">
              <span>✓</span>
              <span>Anında hesabınıza yüklenir</span>
            </div>
            <div className="cpm-feature">
              <span>✓</span>
              <span>Tüm özelliklerle kullanılabilir</span>
            </div>
          </div>
          
          <div className="cpm-security">
            <span className="cpm-lock">🔒</span>
            <span>Güvenli ödeme - 256-bit SSL şifreleme</span>
          </div>
          
          <div className="cpm-payment-methods">
            <div className="cpm-payment-card">💳 Visa</div>
            <div className="cpm-payment-card">💳 Mastercard</div>
            <div className="cpm-payment-card">💳 Amex</div>
            <span className="cpm-powered">Powered by Stripe</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchaseModal;
